import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Channel } from '../models/chat.models';
import { ChatService } from '../services/chat.service';

type ContactUser = {
  user_id: number;
  user_name: string;
  email: string;
  role_name: string;
};

export interface EntityChannelCreatePayload {
  channelName: string;
  memberIds: number[];
}

@Component({
  selector: 'app-entity-channel-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
  ],
  templateUrl: './entity-channel-picker.component.html',
  styleUrls: ['./entity-channel-picker.component.scss'],
})
export class EntityChannelPickerComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() channels: Channel[] = [];
  @Input() entityType = '';
  @Input() entityDisplayName = '';

  @Output() channelSelected = new EventEmitter<number>();
  @Output() createNew = new EventEmitter<EntityChannelCreatePayload>();

  showCreateForm = false;
  newChannelName = '';
  memberSearchQuery = '';
  memberSearchResults: ContactUser[] = [];
  memberSearching = false;
  selectedMembers: ContactUser[] = [];
  creating = false;
  memberPage = 1;
  memberPageSize = 50;
  memberHasMore = true;
  memberLoadingMore = false;

  private lastMemberSearchQuery = '';

  private memberSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.memberSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => {
        const trimmedQuery = query.trim();

        if (!trimmedQuery || trimmedQuery.length < 2) {
          this.memberSearchResults = [];
          this.memberSearching = false;
          this.memberLoadingMore = false;
          this.memberPage = 1;
          this.memberHasMore = false;
          this.lastMemberSearchQuery = '';

          this.cdr.markForCheck();
          return;
        }

        // New search → reset pagination
        this.lastMemberSearchQuery = trimmedQuery;
        this.memberPage = 1;
        this.memberHasMore = true;
        this.memberSearchResults = [];
        this.memberSearching = true;

        this.loadMemberUsers(false);
      });
  }

  private loadMemberUsers(append = false): void {
    if (!this.lastMemberSearchQuery) {
      return;
    }

    if (append) {
      if (this.memberLoadingMore || !this.memberHasMore) {
        return;
      }

      this.memberLoadingMore = true;
    } else {
      this.memberSearching = true;
    }

    this.chatService
      .searchUsers(
        this.lastMemberSearchQuery,
        this.memberPage,
        this.memberPageSize
      )
      .subscribe({
        next: (results) => {
          const selectedIds = new Set(
            this.selectedMembers.map((member) => member.user_id)
          );

          const newUsers = results.users.filter(
            (user) => !selectedIds.has(user.user_id)
          );

          if (append) {
            this.memberSearchResults = [
              ...this.memberSearchResults,
              ...newUsers,
            ];
          } else {
            this.memberSearchResults = newUsers;
          }

          this.memberHasMore = !results.last;

          if (this.memberHasMore) {
            this.memberPage++;
          }

          this.memberSearching = false;
          this.memberLoadingMore = false;

          this.cdr.markForCheck();
        },

        error: () => {
          this.memberSearching = false;
          this.memberLoadingMore = false;

          this.cdr.markForCheck();
        },
      });
  }

  onMemberResultsScroll(event: Event): void {
    const element = event.target as HTMLElement;

    const reachedBottom =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - 50;

    if (!reachedBottom) {
      return;
    }

    if (
      this.memberLoadingMore ||
      !this.memberHasMore ||
      !this.lastMemberSearchQuery
    ) {
      return;
    }

    // console.log('LOADING NEXT PAGE:', this.memberPage);

    this.loadMemberUsers(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get dialogHeader(): string {
    const label = this.entityDisplayName || `${this.entityType} chat`;
    return this.showCreateForm ? `New channel — ${label}` : `Open chat — ${label}`;
  }

  get dialogWidth(): string {
    return this.showCreateForm ? '520px' : '460px';
  }

  onHide(): void {
    this.resetCreateForm();
    this.visibleChange.emit(false);
  }

  selectChannel(channelId: number): void {
    this.channelSelected.emit(channelId);
    this.close();
  }

  startCreate(): void {
    this.showCreateForm = true;
    this.newChannelName = '';
    this.cdr.markForCheck();
  }

  cancelCreate(): void {
    this.resetCreateForm();
    this.cdr.markForCheck();
  }

  onMemberSearchInput(): void {
    this.memberSearch$.next(this.memberSearchQuery);
  }

  addMember(user: ContactUser): void {
    if (this.selectedMembers.some((member) => member.user_id === user.user_id)) {
      return;
    }

    this.selectedMembers = [...this.selectedMembers, user];
    this.memberSearchResults = this.memberSearchResults.filter(
      (result) => result.user_id !== user.user_id
    );
    this.cdr.markForCheck();
  }

  removeMember(userId: number): void {
    this.selectedMembers = this.selectedMembers.filter(
      (member) => member.user_id !== userId
    );
    this.cdr.markForCheck();
  }

  submitCreate(): void {
    if (this.creating) {
      return;
    }

    this.creating = true;
    this.createNew.emit({
      channelName: this.newChannelName.trim() || this.suggestChannelName(),
      memberIds: this.selectedMembers.map((member) => member.user_id),
    });
    this.creating = false;
    this.close();
  }

  getChannelDisplayName(channel: Channel): string {
    if (channel.channel_name) {
      return channel.channel_name;
    }
    if (channel.channel_type === 'entity_linked') {
      return `${channel.linked_entity_type} #${channel.linked_entity_id}`;
    }
    return `Channel #${channel.id}`;
  }

  formatLastMessageAt(value: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }

  private suggestChannelName(): string {
    const base =
      this.entityDisplayName ||
      `${this.entityType} #${this.channels[0]?.linked_entity_id ?? ''}`;
    return `${base} - Channel ${this.channels.length + 1}`;
  }

  private resetCreateForm(): void {
    this.showCreateForm = false;
    this.newChannelName = '';
    this.memberSearchQuery = '';
    this.memberSearchResults = [];
    this.memberSearching = false;
    this.selectedMembers = [];
    this.creating = false;
  }

  private close(): void {
    this.resetCreateForm();
    this.visibleChange.emit(false);
  }
}
